/** @jsxImportSource @emotion/react */
import { useTheme } from "@emotion/react";
import { CircularProgress } from "@material-ui/core";
import Button from "component/Button";
import Input from "component/Input";
import _ from "lodash";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Redirect } from "react-router";
import {
  useGetClientInfo,
  useUpdateInvoivingInformation,
} from "utils/hooks/client.hook";

export type InvoicingInformationFormData = {
  companylegalName: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
};

function InvoicingInformation() {
  const theme = useTheme();
  const { client, isLoading, isError } = useGetClientInfo();
  const { mutate: update } = useUpdateInvoivingInformation();
  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { dirtyFields, errors },
  } = useForm<InvoicingInformationFormData>({
    mode: "onChange",
  });

  useEffect(() => {
    if (client) {
      setValue("companylegalName", client.companylegalName);
      setValue("addressLine1", client.addressLine1);
      setValue("addressLine2", client.addressLine2);
      setValue("addressLine3", client.addressLine3);
    }
  }, [client, setValue]);

  const onSubmit = (data: InvoicingInformationFormData) => {
    update(data);
    reset(data);
  };

  const formDisabled = !_.isEmpty(dirtyFields) && _.isEmpty(errors);

  if (isLoading)
    return (
      <div
        css={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </div>
    );

  if (isError) return <Redirect to="/my_account" />;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      css={{
        padding: "24px 40px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <h3
        css={{
          fontWeight: 600,
          fontSize: "24px",
          lineHeight: "29px",
          color: theme.black,
          marginTop: "0px",
          marginBottom: "48px",
        }}
      >
        Edit invoicing information
      </h3>

      <Input
        label="Company legal name"
        placeholder=""
        name="companylegalName"
        register={register}
        registerOption={{ required: `Company legal name can't be empty` }}
        error={errors.companylegalName && errors.companylegalName.message}
        css={{
          marginBottom: "24px",
          maxWidth: "344px",
        }}
      />

      <Input
        label="Address line 1 (street)"
        placeholder=""
        name="addressLine1"
        register={register}
        registerOption={{ required: `Address line 1 (street) can't be empty` }}
        error={errors.addressLine1 && errors.addressLine1.message}
        css={{
          marginBottom: "24px",
          maxWidth: "344px",
        }}
      />

      <Input
        label="Address line 2 (zip code and city)"
        placeholder=""
        name="addressLine2"
        register={register}
        registerOption={{
          required: `Address line 2 (zip code and city) can't be empty`,
        }}
        error={errors.addressLine2 && errors.addressLine2.message}
        css={{
          marginBottom: "24px",
          maxWidth: "344px",
        }}
      />

      <Input
        label="Address line 3 (country)"
        placeholder=""
        name="addressLine3"
        register={register}
        registerOption={{ required: `Address line 3 (country) can't be empty` }}
        error={errors.addressLine3 && errors.addressLine3.message}
        css={{
          marginBottom: "24px",
          maxWidth: "344px",
        }}
      />

      <Button
        type="submit"
        disabled={!formDisabled}
        variant="dark"
        css={{
          maxWidth: "344px",
        }}
      >
        Save
      </Button>
    </form>
  );
}

export default InvoicingInformation;
